"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PropertyListCard = exports.PropertyTypeRow = exports.REQUIRED_CELL_WIDTH = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const slim_1 = require("@blockprotocol/type-system/slim");
const free_solid_svg_icons_1 = require("@fortawesome/free-solid-svg-icons");
const design_system_1 = require("@hashintel/design-system");
const material_1 = require("@mui/material");
const hooks_1 = require("material-ui-popup-state/hooks");
const react_1 = require("react");
const react_hook_form_1 = require("react-hook-form");
const ontology_functions_context_1 = require("../shared/ontology-functions-context");
const property_types_options_context_1 = require("../shared/property-types-options-context");
const read_only_context_1 = require("../shared/read-only-context");
const disabled_checkbox_cell_1 = require("./property-list-card/disabled-checkbox-cell");
const get_property_type_schema_1 = require("./property-list-card/get-property-type-schema");
const property_expected_values_1 = require("./property-list-card/property-expected-values");
const property_title_cell_1 = require("./property-list-card/property-title-cell");
const property_type_form_1 = require("./property-list-card/property-type-form");
const property_type_to_form_data_expected_values_1 = require("./property-list-card/property-type-to-form-data-expected-values");
const collapsible_row_line_1 = require("./shared/collapsible-row-line");
const empty_list_card_1 = require("./shared/empty-list-card");
const entity_type_table_1 = require("./shared/entity-type-table");
const insert_type_row_1 = require("./shared/insert-type-row");
const multiple_values_cell_1 = require("./shared/multiple-values-cell");
const question_icon_1 = require("./shared/question-icon");
const type_form_1 = require("./shared/type-form");
const type_menu_cell_1 = require("./shared/type-menu-cell");
const use_filter_type_options_1 = require("./shared/use-filter-type-options");
const use_state_callback_1 = require("./shared/use-state-callback");
const use_type_versions_1 = require("./shared/use-type-versions");
const CollapsibleTableRow = ({ expanded, depth, lineHeight, children, }) => {
    return ((0, jsx_runtime_1.jsx)(material_1.TableRow, { children: (0, jsx_runtime_1.jsx)(material_1.TableCell, { colSpan: 12, sx: { p: "0 !important", position: "relative" }, children: (0, jsx_runtime_1.jsxs)(material_1.Collapse, { in: expanded, sx: {
                    position: "relative",
                    top: `-${lineHeight}px`,
                    mb: `-${lineHeight}px`,
                    pointerEvents: "none",
                }, appear: true, children: [(0, jsx_runtime_1.jsx)(collapsible_row_line_1.CollapsibleRowLine, { height: `${lineHeight}px`, depth: depth }), (0, jsx_runtime_1.jsx)(material_1.Table, { sx: { mt: `${lineHeight}px`, pointerEvents: "all" }, children: (0, jsx_runtime_1.jsx)(material_1.TableBody, { sx: {
                                "::before": {
                                    height: 0,
                                },
                            }, children: children }) })] }) }) }));
};
exports.REQUIRED_CELL_WIDTH = 100;
const PropertyRow = ({ property, isArray, isRequired, depth = 0, lines = [], parentPropertyName, allowArraysTableCell, requiredTableCell, menuTableCell, onUpdateVersion, flash = false, }) => {
    const propertyTypesOptions = (0, property_types_options_context_1.usePropertyTypesOptions)();
    const isReadonly = (0, read_only_context_1.useIsReadonly)();
    const [currentVersion, latestVersion, baseUrl] = (0, use_type_versions_1.useTypeVersions)(property.$id, propertyTypesOptions);
    const [expanded, setExpanded] = (0, react_1.useState)(true);
    const mainRef = (0, react_1.useRef)(null);
    const [lineHeight, setLineHeight] = (0, react_1.useState)(0);
    const [animatingOutExpectedValue, setAnimatingOutExpectedValue] = (0, react_1.useState)(false);
    const [selectedExpectedValueIndex, setSelectedExpectedValueIndex] = (0, react_1.useState)(-1);
    const children = (0, react_1.useMemo)(() => {
        const selectedProperty = property.oneOf[selectedExpectedValueIndex]
            ? property.oneOf[selectedExpectedValueIndex]
            : null;
        const selectedObjectProperties = selectedProperty && "properties" in selectedProperty
            ? selectedProperty.properties
            : undefined;
        return selectedObjectProperties
            ? Object.entries(selectedObjectProperties).reduce((childrenArray, [propertyId, ref]) => {
                const $ref = "items" in ref ? ref.items.$ref : ref.$ref;
                const propertyType = propertyTypesOptions[$ref];
                if (propertyType) {
                    const array = "type" in ref;
                    const required = Boolean(selectedProperty &&
                        "required" in selectedProperty &&
                        selectedProperty.required?.includes(propertyId));
                    return [...childrenArray, { ...propertyType, array, required }];
                }
                return childrenArray;
            }, [])
            : [];
    }, [selectedExpectedValueIndex, property.oneOf, propertyTypesOptions]);
    const handleResize = () => {
        if (mainRef.current) {
            setLineHeight(mainRef.current.offsetHeight * 0.5 - 8);
        }
    };
    (0, react_1.useEffect)(() => {
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)(entity_type_table_1.EntityTypeTableRow, { ref: (row) => {
                    if (row) {
                        mainRef.current = row;
                        handleResize();
                    }
                }, flash: flash, children: [(0, jsx_runtime_1.jsx)(property_title_cell_1.PropertyTitleCell, { property: property, array: isArray, depth: depth, lines: lines, expanded: children.length ? expanded : undefined, setExpanded: setExpanded, currentVersion: currentVersion, latestVersion: latestVersion, onUpdateVersion: () => {
                            if (latestVersion) {
                                onUpdateVersion?.(`${baseUrl}v/${latestVersion}`);
                            }
                        } }), (0, jsx_runtime_1.jsx)(material_1.TableCell, { children: (0, jsx_runtime_1.jsx)(property_expected_values_1.PropertyExpectedValues, { property: property, selectedExpectedValueIndex: selectedExpectedValueIndex, setSelectedExpectedValueIndex: (value) => {
                                setSelectedExpectedValueIndex(value);
                                setExpanded(true);
                            }, setAnimatingOutExpectedValue: setAnimatingOutExpectedValue }) }), allowArraysTableCell && !isReadonly && !parentPropertyName ? (allowArraysTableCell) : ((0, jsx_runtime_1.jsx)(disabled_checkbox_cell_1.DisabledCheckboxCell, { title: isReadonly
                            ? undefined
                            : `Edit the '${parentPropertyName ?? "parent"}' property to change this`, checked: isArray, width: multiple_values_cell_1.MULTIPLE_VALUES_CELL_WIDTH, sx: { pr: 1 } })), requiredTableCell && !isReadonly && !parentPropertyName ? (requiredTableCell) : ((0, jsx_runtime_1.jsx)(disabled_checkbox_cell_1.DisabledCheckboxCell, { title: isReadonly
                            ? undefined
                            : `Edit the '${parentPropertyName ?? "parent"}' property to change this`, checked: isRequired, width: exports.REQUIRED_CELL_WIDTH })), menuTableCell ?? ((0, jsx_runtime_1.jsx)(type_menu_cell_1.TypeMenuCell, { typeId: property.$id, variant: "property", editable: false }))] }), children.length ? ((0, jsx_runtime_1.jsx)(CollapsibleTableRow, { expanded: expanded && !animatingOutExpectedValue, depth: depth, lineHeight: lineHeight, children: children.map((prop, pos) => ((0, jsx_runtime_1.jsx)(PropertyRow, { property: prop, depth: depth + 1, lines: [...lines, pos !== children.length - 1], isArray: prop.array, isRequired: prop.required, parentPropertyName: property.title }, prop.$id))) })) : null] }));
};
const PropertyTypeRow = ({ propertyIndex, onRemove, onUpdateVersion, flash, }) => {
    const { control } = (0, react_hook_form_1.useFormContext)();
    const [isRequired, isArray, propertyId] = (0, react_hook_form_1.useWatch)({
        control,
        name: [
            `properties.${propertyIndex}.required`,
            `properties.${propertyIndex}.array`,
            `properties.${propertyIndex}.$id`,
        ],
    });
    const editModalId = (0, react_1.useId)();
    const editModalPopupState = (0, hooks_1.usePopupState)({
        variant: "popover",
        popupId: `edit-property-type-modal-${editModalId}`,
    });
    const onUpdateVersionRef = (0, react_1.useRef)(onUpdateVersion);
    (0, react_1.useLayoutEffect)(() => {
        onUpdateVersionRef.current = onUpdateVersion;
    });
    const propertyTypesOptions = (0, property_types_options_context_1.usePropertyTypesOptions)();
    const property = propertyTypesOptions[propertyId];
    const { updatePropertyType } = (0, ontology_functions_context_1.useOntologyFunctions)();
    const [currentVersion, latestVersion] = (0, use_type_versions_1.useTypeVersions)(propertyId, propertyTypesOptions);
    const getDefaultValues = (0, react_1.useCallback)(() => {
        if (!property) {
            throw new Error("Missing property type");
        }
        const [expectedValues, flattenedCustomExpectedValueList] = (0, property_type_to_form_data_expected_values_1.propertyTypeToFormDataExpectedValues)(property);
        return {
            name: property.title,
            description: property.description,
            expectedValues,
            flattenedCustomExpectedValueList,
        };
    }, [property]);
    if (!property) {
        return null;
    }
    const $id = property.$id;
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(PropertyRow, { property: property, isArray: isArray, isRequired: isRequired, allowArraysTableCell: (0, jsx_runtime_1.jsx)(multiple_values_cell_1.MultipleValuesCell, { index: propertyIndex, variant: "property" }), requiredTableCell: (0, jsx_runtime_1.jsx)(entity_type_table_1.EntityTypeTableCenteredCell, { width: exports.REQUIRED_CELL_WIDTH, children: (0, jsx_runtime_1.jsx)(react_hook_form_1.Controller, { render: ({ field: { value, ...field } }) => ((0, jsx_runtime_1.jsx)(material_1.Checkbox, { ...field, checked: value })), control: control, name: `properties.${propertyIndex}.required` }) }), menuTableCell: (0, jsx_runtime_1.jsx)(type_menu_cell_1.TypeMenuCell, { editButtonProps: (0, hooks_1.bindTrigger)(editModalPopupState), onRemove: onRemove, typeId: property.$id, variant: "property", ...(currentVersion !== latestVersion
                        ? {
                            editButtonDisabled: "Update the property type to the latest version to edit",
                        }
                        : {}) }), onUpdateVersion: onUpdateVersion, flash: flash }), (0, jsx_runtime_1.jsx)(type_form_1.TypeFormModal, { as: property_type_form_1.PropertyTypeForm, baseUrl: (0, slim_1.extractBaseUrl)($id), popupState: editModalPopupState, modalTitle: (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: "Edit Property Type" }), onSubmit: async (data) => {
                    const res = await updatePropertyType({
                        data: {
                            propertyTypeId: $id,
                            propertyType: (0, get_property_type_schema_1.getPropertyTypeSchema)(data),
                        },
                    });
                    if (!res.data) {
                        throw new Error("Failed to update property type");
                    }
                    onUpdateVersionRef.current(res.data.schema.$id);
                    editModalPopupState.close();
                }, submitButtonProps: { children: (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: "Edit property type" }) }, disabledFields: ["name"], getDefaultValues: getDefaultValues })] }));
};
exports.PropertyTypeRow = PropertyTypeRow;
const InsertPropertyRow = (props) => {
    const { control } = (0, react_hook_form_1.useFormContext)();
    const properties = (0, react_hook_form_1.useWatch)({ control, name: "properties" });
    const propertyTypeOptions = (0, property_types_options_context_1.usePropertyTypesOptions)();
    const propertyTypes = Object.values(propertyTypeOptions);
    const filteredPropertyTypes = (0, use_filter_type_options_1.useFilterTypeOptions)({
        typesToExclude: properties,
        typeOptions: propertyTypes,
    });
    return ((0, jsx_runtime_1.jsx)(insert_type_row_1.InsertTypeRow, { ...props, options: filteredPropertyTypes, variant: "property" }));
};
const PropertyListCard = () => {
    const { control, getValues, setValue } = (0, react_hook_form_1.useFormContext)();
    const { fields: unsortedFields, append, remove, } = (0, react_hook_form_1.useFieldArray)({
        control,
        name: "properties",
    });
    const propertyTypeOptions = (0, property_types_options_context_1.usePropertyTypesOptions)();
    const { createPropertyType } = (0, ontology_functions_context_1.useOntologyFunctions)();
    const isReadonly = (0, read_only_context_1.useIsReadonly)();
    const fields = (0, react_1.useMemo)(() => (0, entity_type_table_1.sortRows)(unsortedFields, (propertyId) => propertyTypeOptions[propertyId], (row) => row.title), [propertyTypeOptions, unsortedFields]);
    const [addingNewProperty, setAddingNewProperty] = (0, use_state_callback_1.useStateCallback)(false);
    const [searchText, setSearchText] = (0, react_1.useState)("");
    const addingNewPropertyRef = (0, react_1.useRef)(null);
    const cancelAddingNewProperty = () => {
        setAddingNewProperty(false);
        setSearchText("");
    };
    const modalTooltipId = (0, react_1.useId)();
    const createModalPopupState = (0, hooks_1.usePopupState)({
        variant: "popover",
        popupId: `createProperty-${modalTooltipId}`,
    });
    const [flashingRows, flashRow] = (0, entity_type_table_1.useFlashRow)();
    const handleAddPropertyType = (propertyType) => {
        cancelAddingNewProperty();
        if (!getValues("properties").some(({ $id }) => $id === propertyType.$id)) {
            append({
                $id: propertyType.$id,
                required: false,
                array: false,
                minValue: 0,
                maxValue: 1,
                infinity: true,
            });
            flashRow(propertyType.$id);
        }
    };
    const handleSubmit = async (data) => {
        if (isReadonly) {
            return;
        }
        const res = await createPropertyType({
            data: {
                propertyType: (0, get_property_type_schema_1.getPropertyTypeSchema)(data),
            },
        });
        if (res.errors?.length || !res.data) {
            // @todo handle this
            throw new Error("Could not create");
        }
        handleAddPropertyType(res.data.schema);
    };
    if (!addingNewProperty && fields.length === 0) {
        return ((0, jsx_runtime_1.jsx)(empty_list_card_1.EmptyListCard, { onClick: isReadonly
                ? undefined
                : () => {
                    setAddingNewProperty(true, () => {
                        addingNewPropertyRef.current?.focus();
                    });
                }, icon: (0, jsx_runtime_1.jsx)(design_system_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faList }), headline: isReadonly ? (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: "No properties defined" }) : (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: "Add a property" }), description: (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: "Properties store individual pieces of information about some aspect of an entity" }), subDescription: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: ["e.g. a ", (0, jsx_runtime_1.jsx)("strong", { children: "person" }), " entity might have a", " ", (0, jsx_runtime_1.jsx)("strong", { children: "date of birth" }), " property which expects a", " ", (0, jsx_runtime_1.jsx)("strong", { children: "date" })] }) }));
    }
    return ((0, jsx_runtime_1.jsxs)(entity_type_table_1.EntityTypeTable, { children: [(0, jsx_runtime_1.jsx)(material_1.TableHead, { children: (0, jsx_runtime_1.jsxs)(entity_type_table_1.EntityTypeTableHeaderRow, { children: [(0, jsx_runtime_1.jsx)(material_1.TableCell, { children: "Property name" }), (0, jsx_runtime_1.jsx)(material_1.TableCell, { children: "Expected values" }), (0, jsx_runtime_1.jsxs)(entity_type_table_1.EntityTypeTableCenteredCell, { children: ["Allow multiple", " ", (0, jsx_runtime_1.jsx)(question_icon_1.QuestionIcon, { tooltip: (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: "Allowing multiple permits the entry of more than one value for a given property" }) })] }), (0, jsx_runtime_1.jsx)(entity_type_table_1.EntityTypeTableCenteredCell, { children: "Required" }), (0, jsx_runtime_1.jsx)(material_1.TableCell, {})] }) }), (0, jsx_runtime_1.jsx)(material_1.TableBody, { children: fields.map(({ field, index }) => ((0, jsx_runtime_1.jsx)(exports.PropertyTypeRow, { propertyIndex: index, onRemove: () => {
                        remove(index);
                    }, onUpdateVersion: (nextId) => {
                        setValue(`properties.${index}.$id`, nextId, {
                            shouldDirty: true,
                        });
                    }, flash: flashingRows.includes(field.$id) }, field.id))) }), (0, jsx_runtime_1.jsx)(material_1.TableFooter, { children: addingNewProperty ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(InsertPropertyRow, { inputRef: addingNewPropertyRef, onCancel: cancelAddingNewProperty, onAdd: handleAddPropertyType, createModalPopupState: createModalPopupState, searchText: searchText, onSearchTextChange: setSearchText }), (0, jsx_runtime_1.jsx)(type_form_1.TypeFormModal, { as: property_type_form_1.PropertyTypeForm, modalTitle: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: ["Create new property type", (0, jsx_runtime_1.jsx)(question_icon_1.QuestionIcon, { sx: {
                                            display: "flex",
                                            ml: 1.25,
                                        }, tooltip: (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: "You should only create a new property type if you can't find an existing one which corresponds to the information you're trying to capture." }) })] }), popupState: createModalPopupState, onSubmit: handleSubmit, submitButtonProps: { children: (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: "Create new property type" }) }, getDefaultValues: () => ({
                                expectedValues: [],
                                ...(searchText.length ? { name: searchText } : {}),
                            }) })] })) : (!isReadonly && ((0, jsx_runtime_1.jsx)(entity_type_table_1.EntityTypeTableButtonRow, { icon: (0, jsx_runtime_1.jsx)(design_system_1.StyledPlusCircleIcon, {}), onClick: () => {
                        setAddingNewProperty(true, () => {
                            addingNewPropertyRef.current?.focus();
                        });
                    }, children: "Add a property" }))) })] }));
};
exports.PropertyListCard = PropertyListCard;
